import React, { useEffect, useRef } from 'react';
import { useBTSChatContext } from './BTSChatContext';
import SchoolCard from './SchoolCard';

const SchoolList = () => {
  const {
    schools,
    loading,
    loadingMore,
    filter,
    setFilter,
    searchQuery,
    setSearchQuery,
    hasMore,
    fetchNextPage,
  } = useBTSChatContext();

  const sentinelRef = useRef(null);

  useEffect(() => {
    if (!hasMore || loading || loadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 }
    );

    const currentSentinel = sentinelRef.current;
    if (currentSentinel) {
      observer.observe(currentSentinel);
    }

    return () => {
      if (currentSentinel) {
        observer.unobserve(currentSentinel);
      }
      observer.disconnect();
    };
  }, [hasMore, loading, loadingMore, fetchNextPage]);

  return (
    <div className="flex flex-col h-full min-h-0 border-r border-gray-200">
      {/* Top Controls: Tabs and Search */}
      <div className="p-4 border-b border-gray-100 flex flex-col gap-3">
        {/* All / Unread Toggle */}
        <div className="flex gap-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-1.5 text-xs font-semibold rounded-full border transition-all duration-300 ${
              filter === 'all'
                ? 'bg-[#E8A020] text-white border-[#E8A020]'
                : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setFilter('unread')}
            className={`px-4 py-1.5 text-xs font-semibold rounded-full border transition-all duration-300 ${
              filter === 'unread'
                ? 'bg-[#E8A020] text-white border-[#E8A020]'
                : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
            }`}
          >
            Unread
          </button>
        </div>

        {/* Search Input */}
        <div className="relative">
          <input
            type="text"
            placeholder="Search Schools"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-4 pr-4 py-2 text-sm bg-[#F3F4F6] border border-transparent rounded-full focus:outline-none focus:ring-1 focus:ring-[#E8A020] focus:bg-white placeholder-gray-400 text-gray-700 transition-all duration-300"
          />
        </div>
      </div>

      {/* Schools List Content */}
      <div className="flex-1 overflow-y-auto min-h-0 divide-y divide-gray-100 p-2 space-y-2">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-10 space-y-2">
            <div className="w-8 h-8 border-2 border-[#E8A020] border-t-transparent rounded-full animate-spin"></div>
            <span className="text-sm text-gray-500 font-medium">Loading schools...</span>
          </div>
        ) : schools.length === 0 ? (
          <div className="text-center py-12 text-sm text-gray-500 font-medium">
            No schools found
          </div>
        ) : (
          <>
            {schools.map((school) => (
              <SchoolCard key={school.schoolCode} school={school} />
            ))}

            {/* Loading More Spinner */}
            {loadingMore && (
              <div className="flex justify-center py-3">
                <div className="w-6 h-6 border-2 border-[#E8A020] border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}

            {/* Scroll sentinel element */}
            <div ref={sentinelRef} className="h-2 w-full" />

            {/* No More Results Notice */}
            {!hasMore && schools.length > 0 && (
              <div className="text-center py-4 text-xs font-semibold text-gray-400">
                No more schools
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SchoolList;
